import { css, cx } from '@linaria/core'
import { type FC, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppNavigation } from '#app/navigation/useAppNavigation'
import { useAppDispatch } from '#app/store'
import { type RecoverableConfiguration, setConfigurationTitle, type TruckConfiguration } from '#app/store/slices/configurationSlice'
import { Icon } from '#components/Icon'
import { Input } from '#components/Input'
import { UntitledConfigurationPlaceholder } from '#features/Configuration/ConfigurationTitle'
import WarningIcon from '#icons/warning-triangle-outline-16.svg'
import { makeConfigurationLocation } from '#pages/Configuration/components/ConfigurationNavigation'
import { ConfigurationStatusIcon } from '#pages/Configuration/components/ConfigurationStatusIcon'
import { useDenominationDisplayData } from '#pages/Configuration/components/useDenominationDisplayData'
import { fragments, style } from '#styles'
import { ConfigurationHeaderDropdown } from './ConfigurationHeaderDropdown'

const headerClass = css`
  display: grid;
  grid-template-columns: min-content min-content;
  grid-template-rows: auto 19px;
  grid-column-gap: 4px;
  max-width: 230px;
  overflow: hidden;
  white-space: nowrap;
  align-items: center;
  padding: 0 8px 7.5px 8px;
  height: 42px;
  

  > ._index, >._type {
    ${fragments.textStyle.homelink.s};
  }

  > ._index, > ._status {
    justify-self: center;
  }

  > ._status {
    color: ${style.themeColors.textDefault};
    transition: 0.25s ease opacity;

    &._spinner {
      opacity: 1;
    }
  }

  > ._title {
    display: flex;
    align-self: center;
    max-width: 200px;
    height: 100%;
    

    > ._title-combo {
      max-width: 188px;
      flex: 1;
      position: relative;
      
      > span {
        overflow: hidden;
        text-overflow: ellipsis;
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        top: 0;
        height: 100%;
        width: 100%;
        ${fragments.textStyle.tab.m};
        padding-left: 2px;
        padding-right: 2px;
      }
      
      > input {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        top: 0;
        height: 100%;
        width: 100%;
        padding: 2px;
        z-index: 1;
        min-height: unset;
        
        ${fragments.textStyle.tab.m};
        color: ${style.themeColors.textDefault};
        border: none;
        outline: 1px solid ${style.themeColors.lineInput};
        outline-offset: -1px;
        transition: 0.25s ease outline-color;
        opacity: 0;
        pointer-events: none;

        &:focus {
          outline-color: ${style.themeColors.lineFocus};
          pointer-events: all;
          opacity: 1;
        }

        &::placeholder {
          ${fragments.textStyle.tab.m};
        }

        &._input-invisible {
          visibility: hidden;
          display: none;
        }
      }
    }
  }

  &._selected {
    > ._title > ._title-editing {
      color: ${style.themeColors.textImportant};
    }

    > ._status._spinner > .spinner > svg > ._trail {
      stroke: ${style.themeColors.textImportant};
    }

    > ._status {
      color: ${style.themeColors.textImportant};
    }
  }
`

const isTruckConfiguration = (x: TruckConfiguration | RecoverableConfiguration): x is TruckConfiguration => x.type === 'truck-configuration'

export const ConfigurationHeaderTab: FC<{
  configuration: TruckConfiguration | RecoverableConfiguration
  activeConfiguration?: TruckConfiguration
  allConfigurations: (TruckConfiguration | RecoverableConfiguration)[]
}> = ({ configuration, activeConfiguration, allConfigurations }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const asTruckConfiguration = isTruckConfiguration(configuration)

  const [localTitle, setLocalTitle] = useState<{ title: string | undefined; changed: boolean }>({
    title: undefined,
    changed: false,
  })

  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const index = allConfigurations.findIndex((x) => x.id === configuration.id)

  const saveAndClose = useCallback(() => {
    if (!asTruckConfiguration) {
      return
    }
    if (localTitle.changed && localTitle.title !== configuration.title) {
      dispatch(
        setConfigurationTitle({
          id: configuration.id,
          title: localTitle.title || UntitledConfigurationPlaceholder,
        }),
      )
    }
    setLocalTitle({
      title: undefined,
      changed: false,
    })
  }, [localTitle, asTruckConfiguration, configuration, configuration.id, dispatch])

  const { navigate } = useAppNavigation()

  const onTabClick = useCallback(() => {
    if (!asTruckConfiguration) {
      return
    }
    const location = makeConfigurationLocation(configuration)
    if (!location) {
      return
    }
    navigate(location)
  }, [navigate, asTruckConfiguration, configuration])

  const { marketDenomination, configurationTitle, defaultConfigurationTitle, displayPartPeriod, partPeriodWarning } =
    useDenominationDisplayData(configuration)

  const displayTitle = configuration.creation.loadingStep === 'done' ? configurationTitle : t('configuration.pending')
  const editableTitle =
    (localTitle.changed ? localTitle.title : undefined) ?? (configurationTitle !== defaultConfigurationTitle ? configurationTitle : '')

  const onKeyDown = useCallback((evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (evt.key === 'Enter') {
      evt.currentTarget.blur()
    }
  }, [])

  const onChange = useCallback((evt: React.FormEvent<HTMLInputElement>) => {
    setLocalTitle({
      title: evt.currentTarget.value,
      changed: true,
    })
  }, [])
  const onBlur = useCallback(
    (evt: React.FocusEvent<HTMLInputElement, Element>) => {
      // for very long titles, scroll to the beginning so that the ellipsis is visible
      evt.currentTarget.setSelectionRange(0, 0)
      saveAndClose()
    },
    [saveAndClose],
  )

  const setIsEditing = useCallback((isEdit: boolean) => {
    if (inputRef.current && isEdit) {
      inputRef.current.focus()
      inputRef.current.setSelectionRange(0, inputRef.current.value.length)
    }
  }, [])

  return (
    <div
      className={cx(headerClass, configuration.id === activeConfiguration?.id && '_selected')}
      onClick={onTabClick}
      data-testid={`configuration.navigation.tab.${configuration.id}`}
    >
      <div className="_index">{index + 1}</div>
      <div className="_type">
        {marketDenomination} {displayPartPeriod}
        {partPeriodWarning && <Icon iconUrl={WarningIcon} size="xxs" title={partPeriodWarning} />}
      </div>
      <ConfigurationStatusIcon configuration={configuration} />
      <div className="_title">
        <span className="_title-combo">
          <Input
            ref={inputRef}
            placeholder={defaultConfigurationTitle}
            slim
            value={editableTitle}
            maxLength={40}
            onKeyDown={onKeyDown}
            onChange={onChange}
            onBlur={onBlur}
          />
          <span>{displayTitle}</span>
        </span>

        <ConfigurationHeaderDropdown
          configuration={configuration}
          activeConfiguration={activeConfiguration}
          allConfigurations={allConfigurations}
          setIsEditing={setIsEditing}
        />
      </div>
    </div>
  )
}
